import React, { useState, useEffect } from 'react';
import Board from '@asseinfo/react-kanban';
import '@asseinfo/react-kanban/dist/styles.css';
import { FormGroup, Label, Input, Button, ButtonGroup, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './TicketBoard.css';
import { useDispatch, useSelector } from 'react-redux';
import { getAllTickets, getTicketById } from '../../../store/slices/ticketSlice';
import { useNavigate } from 'react-router';

const TicketBoard = () => {
  const [modal, setModal] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate(); // Initialize the navigate hook


  const { tickets, status } = useSelector((state) => state.tickets);

  useEffect(() => {
    dispatch(getAllTickets());
  }, [dispatch]);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  // State to track selected services
  const [selectedServices, setSelectedServices] = useState({
    baggageLost: false,
    rsa: false,
    vipLounge: false,
    meetAndGreet: false,
  });

  // State to manage visibility of pipelines (columns)
  const [visiblePipelines, setVisiblePipelines] = useState({
    open: true,
    inPipeline: true,
    failed: true,
    closed: true,
    draft: true,
  });

  // Function to group tickets by status for the board
  const groupTicketsByStatus = (tickets) => {
    const statusMap = {
      open: [],
      inPipeline: [],
      failed: [],
      closed: [],
      draft: [],
    };

    tickets.forEach((ticket) => {
      const status = ticket.status.toLowerCase();
      const card = {
        id: ticket.ID,
        title: ticket.ticket_id,
        service: ticket.serviceDetails?.name || 'Unknown Service',
        substatus: ticket.status, // You can map it to more specific substatus if available
        createdAt: new Date(ticket.created_at).toLocaleString(),
        createdBy: `${ticket.Customer.first_name} ${ticket.Customer.last_name}`,
        priority: ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1),
      };

      if (statusMap[status]) {
        statusMap[status].push(card);
      } else {
        statusMap['draft'].push(card); // fallback to 'draft' for unknown statuses
      }
    });

    return [
      { id: 1, title: 'Open', cards: statusMap.open, style: { backgroundColor: '#d4edda' } },
      { id: 2, title: 'In Pipeline', cards: statusMap.inPipeline, style: { backgroundColor: '#fff3cd' } },
      { id: 4, title: 'Closed', cards: statusMap.closed, style: { backgroundColor: '#d1ecf1' } },
      { id: 3, title: 'Failed', cards: statusMap.failed, style: { backgroundColor: '#f8d7da' } },
      { id: 5, title: 'Draft', cards: statusMap.draft, style: { backgroundColor: '#d1ecf1' } },
    ];
  };

  // Generate the board dynamically based on the tickets
  const board = {
    columns: groupTicketsByStatus(tickets),
  };

  const toggleModal = () => setModal(!modal);

  // Function to handle service selection (plain checkbox)
  const handleServiceChange = (event) => {
    setSelectedServices({ ...selectedServices, [event.target.name]: event.target.checked });
  };

  const handleCardClick = async (e, ID) => {
    e.preventDefault()
    await dispatch(getTicketById(ID))
    navigate(`/pages/tickets-details`); // Navigate to the ticket details page with the ticket ID
  };

  // Function to handle pipeline visibility (ButtonGroup)
  const handlePipelineChange = (pipeline) => {
    setVisiblePipelines((prevState) => ({
      ...prevState,
      [pipeline]: !prevState[pipeline],
    }));
  };

  return (
    <>
      {/* Search Input and Filter Button */}
      <div className="search-filter-container">
        <FormGroup className="search-input">
          <Label className="col-form-label m-0">Search Ticket</Label>
          <Input className="form-control" type="text" required placeholder="eg. Baggage Lost, etc" />
        </FormGroup>
        <Button color="primary" className="filter-button" onClick={toggleModal}>
          Filter
        </Button>
      </div>

      {/* Modal for filtering */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg" className="custom-modal">
        <ModalHeader toggle={toggleModal}>Filter Tickets</ModalHeader>
        <ModalBody>
          {/* Checkboxes for Services */}
          <FormGroup>
            <Label>Services</Label>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  name="baggageLost"
                  checked={selectedServices.baggageLost}
                  onChange={handleServiceChange}
                />{' '}
                Baggage Lost
              </Label>
            </FormGroup>
            {/* Add more service options here... */}
          </FormGroup>

          {/* ButtonGroup for Pipeline Visibility */}
          <FormGroup>
            <Label>Show Pipelines</Label>
            <ButtonGroup>
              {['open', 'inPipeline', 'closed', 'failed', 'draft'].map((pipeline) => (
                <Button
                  key={pipeline}
                  color={visiblePipelines[pipeline] ? 'primary' : 'secondary'}
                  onClick={() => handlePipelineChange(pipeline)}
                >
                  {pipeline.charAt(0).toUpperCase() + pipeline.slice(1)}
                </Button>
              ))}
            </ButtonGroup>
          </FormGroup>

          {/* Date Picker Filters */}
          <FormGroup>
            <Label>Start Date</Label>
            <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} selectsStart startDate={startDate} endDate={endDate} />
          </FormGroup>

          <FormGroup>
            <Label>End Date</Label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggleModal}>
            Apply Filter
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Kanban Board */}
      <Board
        initialBoard={board}
        renderColumnHeader={({ title }, { style }) => (
          <div className="kanban-column-header" style={style}>
            {title}
          </div>
        )}
        renderCard={({ id, title, service, substatus, createdAt, createdBy, priority }) => (
          <div className="kanban-card" onClick={(e) => handleCardClick(e, id)}>
            <div className="kanban-card-id">{title}</div> {/* Ticket ID */}
            <div className="kanban-card-service">{service}</div> {/* Service name */}
            <div className="kanban-card-substatus">{substatus}</div> {/* Substatus */}
            <div className="kanban-card-created-by">{createdBy}</div> {/* Created By */}
            <div className="kanban-card-date">Created: {createdAt}</div> {/* Created Time */}
            <div className={`kanban-card-priority ${priority.toLowerCase()}`}>{priority}</div> {/* Priority */}
          </div>
        )}
        // Conditionally render columns based on visiblePipelines state
        columns={board.columns.filter((column) => visiblePipelines[column.title.toLowerCase().replace(' ', '')])}
      />
    </>
  );
};

export default TicketBoard;
